import { Fragment } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import type { SitePublic } from '@wooconnect/shared';
import { cn } from '@/utils/cn';

interface SiteSelectorProps {
  sites: SitePublic[];
  activeSiteId: string | null;
  onSelect: (site: SitePublic) => void;
}

export function SiteSelector({ sites, activeSiteId, onSelect }: SiteSelectorProps) {
  const { t } = useTranslation();
  const active = sites.find((s) => s.id === activeSiteId);

  return (
    <Listbox
      value={activeSiteId ?? ''}
      onChange={(id) => {
        const site = sites.find((s) => s.id === id);
        if (site) onSelect(site);
      }}
    >
      <div className="relative">
        <ListboxButton className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-sm hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-750">
          <span className={cn('h-2 w-2 rounded-full', active?.status === 'active' ? 'bg-green-500' : 'bg-gray-400')} />
          <span className="flex-1 truncate text-gray-700 dark:text-gray-200">
            {active?.name ?? t('dashboard.selectStore')}
          </span>
          <ChevronUpDownIcon className="h-4 w-4 text-gray-400" />
        </ListboxButton>
        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
          <ListboxOptions className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-800">
            {sites.map((site) => (
              <ListboxOption
                key={site.id}
                value={site.id}
                className={({ focus }) => cn('cursor-pointer select-none px-3 py-2', focus && 'bg-brand-50 dark:bg-brand-900/20')}
              >
                {({ selected }) => (
                  <div className="flex items-center gap-2">
                    <span className={cn('h-2 w-2 rounded-full', site.status === 'active' ? 'bg-green-500' : site.status === 'error' ? 'bg-red-500' : 'bg-gray-400')} />
                    <span className={cn('flex-1 truncate', selected ? 'font-medium text-brand-600 dark:text-brand-400' : 'text-gray-700 dark:text-gray-200')}>
                      {site.name}
                    </span>
                    {selected && <CheckIcon className="h-4 w-4 text-brand-600" />}
                  </div>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </div>
    </Listbox>
  );
}
